import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { 
  X,
  Plus,
  Edit,
  Trash2,
  Search,
  Filter,
  Star,
  RotateCcw,
  Target,
  Clock,
  CheckCircle,
  BookOpen,
  Bookmark,
  Eye,
  Save,
  XCircle,
  TrendingUp,
  Calendar,
  Check
} from 'lucide-react';

const Flashcards = () => {
  const [flashcards, setFlashcards] = useState([]);
  const [currentCardIndex, setCurrentCardIndex] = useState(0);
  const [showAnswer, setShowAnswer] = useState(false);
  const [selectedCategory, setSelectedCategory] = useState('all');
  const [searchTerm, setSearchTerm] = useState('');
  const [showCreateForm, setShowCreateForm] = useState(false);
  const [editingCard, setEditingCard] = useState(null);
  const [studyMode, setStudyMode] = useState('review'); // review, spaced, practice
  const [progress, setProgress] = useState({});

  const [newCard, setNewCard] = useState({
    id: '',
    question: '',
    answer: '',
    category: '',
    difficulty: 'Medium',
    tags: [],
    lastReviewed: null,
    nextReview: null,
    reviewCount: 0,
    correctCount: 0,
    incorrectCount: 0,
    easeFactor: 2.5,
    interval: 1
  });

  const categories = [
    'Mathematics', 'Physics', 'Chemistry', 'Biology', 
    'English', 'History', 'Geography', 'Computer Science',
    'Economics', 'Literature', 'General Knowledge'
  ];

  const difficulties = ['Easy', 'Medium', 'Hard'];

  // Spaced Repetition Algorithm (SuperMemo 2)
  const calculateNextReview = (card, quality) => {
    const { reviewCount, easeFactor, interval } = card;
    
    let newEaseFactor = easeFactor;
    let newInterval = interval;
    
    if (quality >= 3) {
      // Correct response
      if (reviewCount === 0) {
        newInterval = 1;
      } else if (reviewCount === 1) {
        newInterval = 6;
      } else {
        newInterval = Math.round(interval * easeFactor);
      }
      newEaseFactor = Math.max(1.3, easeFactor + (0.1 - (5 - quality) * (0.08 + (5 - quality) * 0.02)));
    } else {
      // Incorrect response
      newInterval = 1;
      newEaseFactor = Math.max(1.3, easeFactor - 0.2);
    }

    const nextReview = new Date();
    nextReview.setDate(nextReview.getDate() + newInterval);

    return {
      reviewCount: reviewCount + 1,
      easeFactor: newEaseFactor,
      interval: newInterval,
      nextReview: nextReview,
      lastReviewed: new Date()
    };
  };

  const handleCardResponse = (quality) => {
    if (currentCardIndex >= flashcards.length) return;

    const currentCard = flashcards[currentCardIndex];
    const updates = calculateNextReview(currentCard, quality);
    
    const updatedCard = {
      ...currentCard,
      ...updates,
      correctCount: quality >= 3 ? currentCard.correctCount + 1 : currentCard.correctCount,
      incorrectCount: quality < 3 ? currentCard.incorrectCount + 1 : currentCard.incorrectCount
    };

    setFlashcards(prev => 
      prev.map(card => 
        card.id === currentCard.id ? updatedCard : card
      )
    );

    // Update progress
    setProgress(prev => ({
      ...prev,
      [currentCard.id]: {
        ...prev[currentCard.id],
        lastQuality: quality,
        lastReviewed: new Date()
      }
    }));

    // Move to next card
    if (currentCardIndex < flashcards.length - 1) {
      setCurrentCardIndex(prev => prev + 1);
      setShowAnswer(false);
    }
  };

  const handleCreateCard = () => {
    const card = {
      ...newCard,
      id: Date.now().toString(),
      createdAt: new Date(),
      reviewCount: 0,
      correctCount: 0,
      incorrectCount: 0,
      easeFactor: 2.5,
      interval: 1,
      nextReview: new Date()
    };

    setFlashcards(prev => [...prev, card]);
    setNewCard({
      id: '',
      question: '',
      answer: '',
      category: '',
      difficulty: 'Medium',
      tags: [],
      lastReviewed: null,
      nextReview: null,
      reviewCount: 0,
      correctCount: 0,
      incorrectCount: 0,
      easeFactor: 2.5,
      interval: 1
    });
    setShowCreateForm(false);
  };

  const handleEditCard = () => {
    if (!editingCard) return;

    setFlashcards(prev => 
      prev.map(card => 
        card.id === editingCard.id ? editingCard : card
      )
    );
    setEditingCard(null);
  };

  const handleDeleteCard = (cardId) => {
    setFlashcards(prev => prev.filter(card => card.id !== cardId));
  };

  const getCardsForStudy = () => {
    let filteredCards = flashcards;

    // Filter by category
    if (selectedCategory !== 'all') {
      filteredCards = filteredCards.filter(card => card.category === selectedCategory);
    }

    // Filter by search term
    if (searchTerm) {
      filteredCards = filteredCards.filter(card => 
        card.question.toLowerCase().includes(searchTerm.toLowerCase()) ||
        card.answer.toLowerCase().includes(searchTerm.toLowerCase())
      );
    }

    // Filter by study mode
    if (studyMode === 'spaced') {
      filteredCards = filteredCards.filter(card => 
        !card.nextReview || new Date(card.nextReview) <= new Date()
      );
    }

    return filteredCards;
  };

  const studyCards = getCardsForStudy();
  const currentCard = studyCards[currentCardIndex];

  const getProgressStats = () => {
    const totalCards = flashcards.length;
    const reviewedCards = flashcards.filter(card => card.reviewCount > 0).length;
    const correctAnswers = flashcards.reduce((sum, card) => sum + card.correctCount, 0);
    const totalAnswers = flashcards.reduce((sum, card) => sum + card.reviewCount, 0);
    const accuracy = totalAnswers > 0 ? (correctAnswers / totalAnswers * 100).toFixed(1) : 0;

    return { totalCards, reviewedCards, accuracy, correctAnswers, totalAnswers };
  };

  const stats = getProgressStats();

  return (
    <div className="min-h-screen bg-gray-50 dark:bg-gray-900">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Header */}
        <div className="mb-8">
          <h1 className="text-3xl font-bold text-gray-900 dark:text-white mb-2">
            Flashcards
          </h1>
          <p className="text-gray-600 dark:text-gray-400">
            Master concepts with spaced repetition learning
          </p>
        </div>

        {/* Stats Overview */}
        <div className="grid grid-cols-1 md:grid-cols-4 gap-6 mb-8">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="bg-white dark:bg-gray-800 p-6 rounded-lg shadow-md"
          >
            <div className="flex items-center">
              <BookOpen className="h-8 w-8 text-blue-600" />
              <div className="ml-4">
                <p className="text-sm font-medium text-gray-600 dark:text-gray-400">Total Cards</p>
                <p className="text-2xl font-bold text-gray-900 dark:text-white">{stats.totalCards}</p>
              </div>
            </div>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.1 }}
            className="bg-white dark:bg-gray-800 p-6 rounded-lg shadow-md"
          >
            <div className="flex items-center">
              <Target className="h-8 w-8 text-green-600" />
              <div className="ml-4">
                <p className="text-sm font-medium text-gray-600 dark:text-gray-400">Reviewed</p>
                <p className="text-2xl font-bold text-gray-900 dark:text-white">{stats.reviewedCards}</p>
              </div>
            </div>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.2 }}
            className="bg-white dark:bg-gray-800 p-6 rounded-lg shadow-md"
          >
            <div className="flex items-center">
              <TrendingUp className="h-8 w-8 text-purple-600" />
              <div className="ml-4">
                <p className="text-sm font-medium text-gray-600 dark:text-gray-400">Accuracy</p>
                <p className="text-2xl font-bold text-gray-900 dark:text-white">{stats.accuracy}%</p>
              </div>
            </div>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.3 }}
            className="bg-white dark:bg-gray-800 p-6 rounded-lg shadow-md"
          >
            <div className="flex items-center">
              <Calendar className="h-8 w-8 text-orange-600" />
              <div className="ml-4">
                <p className="text-sm font-medium text-gray-600 dark:text-gray-400">Due Today</p>
                <p className="text-2xl font-bold text-gray-900 dark:text-white">
                  {flashcards.filter(card => 
                    card.nextReview && new Date(card.nextReview) <= new Date()
                  ).length}
                </p>
              </div>
            </div>
          </motion.div>
        </div>

        {/* Controls */}
        <div className="bg-white dark:bg-gray-800 p-6 rounded-lg shadow-md mb-8">
          <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between space-y-4 lg:space-y-0">
            <div className="flex flex-col sm:flex-row space-y-2 sm:space-y-0 sm:space-x-4">
              <select
                value={selectedCategory}
                onChange={(e) => setSelectedCategory(e.target.value)}
                className="px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
              >
                <option value="all">All Categories</option>
                {categories.map(category => (
                  <option key={category} value={category}>{category}</option>
                ))}
              </select>

              <select
                value={studyMode}
                onChange={(e) => setStudyMode(e.target.value)}
                className="px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
              >
                <option value="review">Review All</option>
                <option value="spaced">Spaced Repetition</option>
                <option value="practice">Practice Mode</option>
              </select>
            </div>

            <div className="flex space-x-2">
              <button
                onClick={() => setShowCreateForm(true)}
                className="flex items-center px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700"
              >
                <Plus className="h-4 w-4 mr-2" />
                Create Card
              </button>
            </div>
          </div>

          <div className="mt-4">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
              <input
                type="text"
                placeholder="Search flashcards..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="w-full pl-10 pr-4 py-2 border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
              />
            </div>
          </div>
        </div>

        {/* Flashcard Study Area */}
        {studyCards.length > 0 ? (
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
            {/* Main Study Area */}
            <div className="lg:col-span-2">
              <motion.div
                key={currentCard?.id || 'empty'}
                initial={{ opacity: 0, scale: 0.9 }}
                animate={{ opacity: 1, scale: 1 }}
                transition={{ duration: 0.3 }}
                className="bg-white dark:bg-gray-800 rounded-lg shadow-lg overflow-hidden"
              >
                {/* Card Header */}
                <div className="bg-gradient-to-r from-blue-600 to-purple-600 p-6 text-white">
                  <div className="flex items-center justify-between">
                    <div>
                      <h3 className="text-lg font-semibold">Card {currentCardIndex + 1} of {studyCards.length}</h3>
                      <p className="text-blue-100">{currentCard?.category} • {currentCard?.difficulty}</p>
                    </div>
                    <div className="text-right">
                      <p className="text-sm text-blue-100">Review Count: {currentCard?.reviewCount || 0}</p>
                      <p className="text-sm text-blue-100">
                        Accuracy: {currentCard?.reviewCount > 0 
                          ? Math.round((currentCard.correctCount / currentCard.reviewCount) * 100)
                          : 0}%
                      </p>
                    </div>
                  </div>
                </div>

                {/* Card Content */}
                <div className="p-8">
                  <div className="mb-6">
                    <h4 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">Question:</h4>
                    <p className="text-gray-700 dark:text-gray-300 text-lg">{currentCard?.question}</p>
                  </div>

                  <AnimatePresence>
                    {showAnswer && (
                      <motion.div
                        initial={{ opacity: 0, height: 0 }}
                        animate={{ opacity: 1, height: 'auto' }}
                        exit={{ opacity: 0, height: 0 }}
                        className="mb-6"
                      >
                        <h4 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">Answer:</h4>
                        <p className="text-gray-700 dark:text-gray-300 text-lg">{currentCard?.answer}</p>
                      </motion.div>
                    )}
                  </AnimatePresence>

                  {/* Action Buttons */}
                  <div className="flex flex-col sm:flex-row space-y-2 sm:space-y-0 sm:space-x-4">
                    {!showAnswer ? (
                      <button
                        onClick={() => setShowAnswer(true)}
                        className="flex-1 px-6 py-3 bg-blue-600 text-white rounded-md hover:bg-blue-700"
                      >
                        Show Answer
                      </button>
                    ) : (
                      <>
                        <button
                          onClick={() => handleCardResponse(1)}
                          className="flex-1 px-6 py-3 bg-red-600 text-white rounded-md hover:bg-red-700"
                        >
                          <X className="h-4 w-4 mr-2 inline" />
                          Hard (1)
                        </button>
                        <button
                          onClick={() => handleCardResponse(2)}
                          className="flex-1 px-6 py-3 bg-yellow-600 text-white rounded-md hover:bg-yellow-700"
                        >
                          <Clock className="h-4 w-4 mr-2 inline" />
                          Medium (2)
                        </button>
                        <button
                          onClick={() => handleCardResponse(3)}
                          className="flex-1 px-6 py-3 bg-green-600 text-white rounded-md hover:bg-green-700"
                        >
                          <Check className="h-4 w-4 mr-2 inline" />
                          Easy (3)
                        </button>
                      </>
                    )}
                  </div>
                </div>
              </motion.div>
            </div>

            {/* Progress Sidebar */}
            <div className="space-y-6">
              {/* Progress Overview */}
              <div className="bg-white dark:bg-gray-800 p-6 rounded-lg shadow-md">
                <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">Progress</h3>
                <div className="space-y-3">
                  <div className="flex justify-between">
                    <span className="text-gray-600 dark:text-gray-400">Completed</span>
                    <span className="font-medium">{currentCardIndex} / {studyCards.length}</span>
                  </div>
                  <div className="w-full bg-gray-200 dark:bg-gray-700 rounded-full h-2">
                    <div 
                      className="bg-blue-600 h-2 rounded-full transition-all duration-300"
                      style={{ width: `${(currentCardIndex / studyCards.length) * 100}%` }}
                    ></div>
                  </div>
                </div>
              </div>

              {/* Card List */}
              <div className="bg-white dark:bg-gray-800 p-6 rounded-lg shadow-md">
                <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">Cards</h3>
                <div className="space-y-2 max-h-64 overflow-y-auto">
                  {studyCards.map((card, index) => (
                    <div
                      key={card.id}
                      className={`p-3 rounded-md cursor-pointer transition-colors ${
                        index === currentCardIndex
                          ? 'bg-blue-100 dark:bg-blue-900 text-blue-900 dark:text-blue-100'
                          : 'bg-gray-50 dark:bg-gray-700 hover:bg-gray-100 dark:hover:bg-gray-600'
                      }`}
                      onClick={() => {
                        setCurrentCardIndex(index);
                        setShowAnswer(false);
                      }}
                    >
                      <p className="text-sm font-medium truncate">{card.question}</p>
                      <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                        {card.category} • Review: {card.reviewCount}
                      </p>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>
        ) : (
          <div className="text-center py-12">
            <BookOpen className="h-16 w-16 text-gray-400 mx-auto mb-4" />
            <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-2">
              No flashcards found
            </h3>
            <p className="text-gray-600 dark:text-gray-400 mb-4">
              Create your first flashcard to start studying
            </p>
            <button
              onClick={() => setShowCreateForm(true)}
              className="px-6 py-3 bg-blue-600 text-white rounded-md hover:bg-blue-700"
            >
              Create Flashcard
            </button>
          </div>
        )}

        {/* Create/Edit Card Modal */}
        <AnimatePresence>
          {(showCreateForm || editingCard) && (
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50"
            >
              <motion.div
                initial={{ scale: 0.9, opacity: 0 }}
                animate={{ scale: 1, opacity: 1 }}
                exit={{ scale: 0.9, opacity: 0 }}
                className="bg-white dark:bg-gray-800 rounded-lg shadow-xl max-w-2xl w-full max-h-[90vh] overflow-y-auto"
              >
                <div className="p-6">
                  <h2 className="text-xl font-semibold text-gray-900 dark:text-white mb-4">
                    {editingCard ? 'Edit Flashcard' : 'Create New Flashcard'}
                  </h2>
                  
                  <div className="space-y-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                        Question
                      </label>
                      <textarea
                        value={editingCard?.question || newCard.question}
                        onChange={(e) => {
                          if (editingCard) {
                            setEditingCard({ ...editingCard, question: e.target.value });
                          } else {
                            setNewCard({ ...newCard, question: e.target.value });
                          }
                        }}
                        rows={3}
                        className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                        placeholder="Enter your question..."
                      />
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                        Answer
                      </label>
                      <textarea
                        value={editingCard?.answer || newCard.answer}
                        onChange={(e) => {
                          if (editingCard) {
                            setEditingCard({ ...editingCard, answer: e.target.value });
                          } else {
                            setNewCard({ ...newCard, answer: e.target.value });
                          }
                        }}
                        rows={3}
                        className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                        placeholder="Enter your answer..."
                      />
                    </div>

                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                          Category
                        </label>
                        <select
                          value={editingCard?.category || newCard.category}
                          onChange={(e) => {
                            if (editingCard) {
                              setEditingCard({ ...editingCard, category: e.target.value });
                            } else {
                              setNewCard({ ...newCard, category: e.target.value });
                            }
                          }}
                          className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                        >
                          <option value="">Select Category</option>
                          {categories.map(category => (
                            <option key={category} value={category}>{category}</option>
                          ))}
                        </select>
                      </div>

                      <div>
                        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                          Difficulty
                        </label>
                        <select
                          value={editingCard?.difficulty || newCard.difficulty}
                          onChange={(e) => {
                            if (editingCard) {
                              setEditingCard({ ...editingCard, difficulty: e.target.value });
                            } else {
                              setNewCard({ ...newCard, difficulty: e.target.value });
                            }
                          }}
                          className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                        >
                          {difficulties.map(difficulty => (
                            <option key={difficulty} value={difficulty}>{difficulty}</option>
                          ))}
                        </select>
                      </div>
                    </div>
                  </div>

                  <div className="flex justify-end space-x-3 mt-6">
                    <button
                      onClick={() => {
                        setShowCreateForm(false);
                        setEditingCard(null);
                      }}
                      className="px-4 py-2 text-gray-600 dark:text-gray-400 hover:text-gray-800 dark:hover:text-gray-200"
                    >
                      Cancel
                    </button>
                    <button
                      onClick={editingCard ? handleEditCard : handleCreateCard}
                      className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700"
                    >
                      {editingCard ? 'Update' : 'Create'}
                    </button>
                  </div>
                </div>
              </motion.div>
            </motion.div>
          )}
        </AnimatePresence>
      </div>
    </div>
  );
};

export default Flashcards;
